import { DEVICE_STATUS, ORDER_STATUS } from '../../../../shared/constants';

/**
 * The `(status, device_status)` tuple, classified once.
 *
 * The platform reports two independent numbers — the order's status and the
 * machine's status — and the pair is what actually decides the flow. Modelling it
 * as one named state is Rule 5 of `.claude/rules/branching-logic.md`; the previous
 * shape was six overlapping booleans and a `switch (true)` whose outcome depended
 * on case order.
 *
 * States are named for what is objectively true of the tuple, NOT for what any one
 * flow does about it — because the flows disagree. `charged-session-closed` is a
 * completed vend to a CT1 machine and a refund to a CT5 one; naming the state after
 * either reading would bake one flow's opinion into shared code.
 *
 * `unmapped` is not an error state. It means the platform sent a pair no rule
 * covers, and the screen stays put until the wait timer expires. That is existing
 * behaviour and must be preserved — see `.claude/audit/2026-08-24-branching-logic-refactor.md`
 * §5.6 for the open question of whether it should stay that way.
 */
export const ORDER_STATE = {
    /** Paid; the machine has not reported anything yet. */
    PAID_AWAITING_DEVICE: 'paid-awaiting-device',
    /** Paid; the customer picked a product on the machine. */
    PAID_PRODUCT_SELECTED: 'paid-product-selected',
    /** Paid; the machine reported a live cashless session. */
    PAID_CONNECTED: 'paid-connected',
    /** Paid, but the machine already reports a later stage than the order does. */
    PAID_DEVICE_AHEAD: 'paid-device-ahead',
    /** The payment itself did not go through. */
    PAYMENT_FAILED: 'payment-failed',
    /** Charged; the machine reported the goods were handed over. */
    CHARGED_DISPENSED: 'charged-dispensed',
    /** Charged; the machine reported the session closed. */
    CHARGED_SESSION_CLOSED: 'charged-session-closed',
    /** Charged; the machine still reports a product selection. */
    CHARGED_PRODUCT_SELECTED: 'charged-product-selected',
    /** Charged; the machine still reports only a live session. */
    CHARGED_CONNECTED: 'charged-connected',
    /** Charged; the machine has reported nothing useful. */
    CHARGED_DEVICE_SILENT: 'charged-device-silent',
    /** Money returned; the machine had reported the goods were handed over. */
    REFUNDED_DISPENSED: 'refunded-dispensed',
    /** Money returned; the machine had reported the session closed. */
    REFUNDED_SESSION_CLOSED: 'refunded-session-closed',
    /** Money returned; the machine reported something else. */
    REFUNDED_DEVICE_OTHER: 'refunded-device-other',
    /**
     * An operator returned the money by hand from the back office.
     *
     * The only state deliberately blind to `device_status`. Every other one pairs
     * the order's status with what the machine last reported, because for an
     * automatic refund the two together decide the flow. Here they do not: status 6
     * is written by a human in the back office, alongside `user_rtrn_id` and
     * `date_rtrn`, and it is already the whole fact. What the machine happened to
     * say last is not evidence about it.
     *
     * Collapsing all five observed tuples into one state is the point. Production
     * has `(6,4)` 2,211, `(6,null)` 704, `(6,6)` 375, `(6,3)` 185 and `(6,5)` 139.
     * Routing status 6 through the automatic-refund states would classify the last
     * two as `refunded-device-other`, which both flows leave on the wait screen —
     * 324 orders still ending on the failure screen for no reason.
     */
    REFUNDED_MANUALLY: 'refunded-manually',
    /** No rule covers this pair. */
    UNMAPPED: 'unmapped',
} as const;

export type OrderState = (typeof ORDER_STATE)[keyof typeof ORDER_STATE];

export const resolveOrderState = (
    status: number,
    deviceStatus: number | null,
): OrderState => {
    if (status === ORDER_STATUS.PAYMENT_FAIL) {
        return ORDER_STATE.PAYMENT_FAILED;
    }

    if (status === ORDER_STATUS.PREPAID) {
        // A falsy device status covers both null and 0 — the machine is silent.
        if (!deviceStatus) return ORDER_STATE.PAID_AWAITING_DEVICE;
        if (deviceStatus === DEVICE_STATUS.PRODUCT_SELECTED) {
            return ORDER_STATE.PAID_PRODUCT_SELECTED;
        }
        if (deviceStatus === DEVICE_STATUS.CONNECTED) {
            return ORDER_STATE.PAID_CONNECTED;
        }
        return ORDER_STATE.PAID_DEVICE_AHEAD;
    }

    if (status === ORDER_STATUS.COMPLETED) {
        if (deviceStatus === DEVICE_STATUS.PAID) {
            return ORDER_STATE.CHARGED_DISPENSED;
        }
        if (deviceStatus === DEVICE_STATUS.COMPLETED) {
            return ORDER_STATE.CHARGED_SESSION_CLOSED;
        }
        if (deviceStatus === DEVICE_STATUS.PRODUCT_SELECTED) {
            return ORDER_STATE.CHARGED_PRODUCT_SELECTED;
        }
        if (deviceStatus === DEVICE_STATUS.CONNECTED) {
            return ORDER_STATE.CHARGED_CONNECTED;
        }
        return ORDER_STATE.CHARGED_DEVICE_SILENT;
    }

    if (status === ORDER_STATUS.REFUNDED_MANUALLY) {
        return ORDER_STATE.REFUNDED_MANUALLY;
    }

    if (
        status === ORDER_STATUS.REFUNDED ||
        status === ORDER_STATUS.REFUND_DEFFERED
    ) {
        if (deviceStatus === DEVICE_STATUS.PAID) {
            return ORDER_STATE.REFUNDED_DISPENSED;
        }
        if (deviceStatus === DEVICE_STATUS.COMPLETED) {
            return ORDER_STATE.REFUNDED_SESSION_CLOSED;
        }
        return ORDER_STATE.REFUNDED_DEVICE_OTHER;
    }

    return ORDER_STATE.UNMAPPED;
};
